import { ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { take } from 'rxjs/operators';
import { DialogCloseButtonComponent } from '../../shared/components/dialog-close-button/dialog-close-button.component';

/** Put this into a dialog's `panelClass` to opt that single dialog out of the generic close button. */
export const NO_DIALOG_CLOSE_BUTTON_PANEL_CLASS = 'scw-dialog-no-close-button';

/** Added to dialogs that received the close button so the title can reserve room for it (see `generic.scss`). */
const HAS_DIALOG_CLOSE_BUTTON_PANEL_CLASS = 'scw-dialog-has-close-button';

/**
 * Adds a close (X) button to the top-right corner of every dialog opened through `MatDialog`,
 * so individual dialog templates do not have to declare one.
 *
 * The button is appended inside the dialog container (inside the focus trap, outside the scrollable surface)
 * and closes the dialog with an `undefined` result — the same result a backdrop click or Escape produces.
 */
@Injectable({ providedIn: 'root' })
export class DialogCloseButtonService {
  private readonly appRef: ApplicationRef = inject(ApplicationRef);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly environmentInjector: EnvironmentInjector = inject(EnvironmentInjector);

  private isStarted = false;
  private locatorId = 0;

  /** Starts listening for opened dialogs. Called once on app start. */
  public start(): void {
    if (this.isStarted) {
      return;
    }

    this.isStarted = true;

    this.dialog.afterOpened.subscribe((dialogRef: MatDialogRef<unknown>): void => this.attachCloseButton(dialogRef));
  }

  private attachCloseButton(dialogRef: MatDialogRef<unknown>): void {
    const pane: HTMLElement | null = this.getPaneElement(dialogRef);

    if (!pane || pane.classList.contains(NO_DIALOG_CLOSE_BUTTON_PANEL_CLASS)) {
      return;
    }

    const container: HTMLElement | null = pane.querySelector<HTMLElement>('.mat-mdc-dialog-container');

    if (!container) {
      return;
    }

    const componentRef: ComponentRef<DialogCloseButtonComponent> = createComponent(DialogCloseButtonComponent, {
      environmentInjector: this.environmentInjector,
    });

    // The inner container is the element carrying the dialog's own fade transition, so the button fades in and out
    // together with the dialog instead of showing up before the open animation has finished.
    const animationHost: HTMLElement =
      container.querySelector<HTMLElement>('.mat-mdc-dialog-inner-container') ?? container;

    componentRef.setInput('dialogRef', dialogRef);
    this.appRef.attachView(componentRef.hostView);
    animationHost.appendChild(componentRef.location.nativeElement);
    componentRef.changeDetectorRef.detectChanges();
    dialogRef.addPanelClass(HAS_DIALOG_CLOSE_BUTTON_PANEL_CLASS);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((): void => {
        if (!this.appRef.destroyed) {
          this.appRef.detachView(componentRef.hostView);
        }

        componentRef.destroy();
      });
  }

  /**
   * Resolves the overlay pane of a just opened dialog.
   * `MatDialogRef.id` cannot be used for this, because the container renders it as an `id` attribute only on the next
   * change detection run, while `afterOpened` emits synchronously. Panel classes are written to the pane right away.
   */
  private getPaneElement(dialogRef: MatDialogRef<unknown>): HTMLElement | null {
    const locatorClass = `scw-dialog-locator-${this.locatorId++}`;

    dialogRef.addPanelClass(locatorClass);
    const pane: HTMLElement | null = document.querySelector<HTMLElement>(`.${locatorClass}`);
    dialogRef.removePanelClass(locatorClass);

    return pane;
  }
}
